import { useMemo } from 'react';
import { useOptionalPublicBranding } from '@/contexts/PublicBrandingContext';
import { useOptionalSiteSettings } from '@/contexts/SiteSettingsContext';
import { resolveUploadUrl } from '@/lib/assets/resolveUploadUrl';
import { DEFAULT_BRAND_TITLE } from '@/lib/branding/loadingBlockConstants';
import { resolveBrandAwareLoading } from '@/lib/branding/resolveBrandAwareLoading';

/**
 * UI chrome brand data (Login, sidebar, auth headers).
 * Title and logo use the same normalizer as Loading Block (scope=content).
 * Does not mutate DOM — consumers render only.
 */
export const useBrand = () => {
  const publicBranding = useOptionalPublicBranding();
  const siteSettings = useOptionalSiteSettings();

  return useMemo(() => {
    const resolved = resolveBrandAwareLoading({
      scope: 'content',
      siteBranding: siteSettings?.settings.branding,
      siteUi: siteSettings?.settings.ui,
      publicBranding: publicBranding?.branding ?? null,
    });

    const normalized = publicBranding?.branding;
    const siteBranding = siteSettings?.settings.branding;

    const faviconSrc =
      normalized?.favicon_src ??
      (siteBranding?.favicon_url ? resolveUploadUrl(siteBranding.favicon_url) : null);

    const loadingImageSrc =
      normalized?.loading_src ??
      (siteBranding?.loading_image_url ? resolveUploadUrl(siteBranding.loading_image_url) : null);

    if (import.meta.env.DEV) {
      console.debug('[useBrand] via resolveBrandAwareLoading:', {
        title: resolved.title,
        brandAssetSource: resolved.brandAssetSource,
        configSource: resolved.configSource,
      });
    }

    return {
      name: resolved.title ?? DEFAULT_BRAND_TITLE,
      logoSrc: resolved.brandAssetUrl,
      faviconSrc,
      loadingImageSrc,
      hasLogo: !!resolved.brandAssetUrl,
    };
  }, [publicBranding?.branding, siteSettings?.settings.branding, siteSettings?.settings.ui]);
};
